import * as Effect from 'effect/Effect';
import * as Context from 'effect/Context';
import * as Layer from 'effect/Layer';
import { pipe } from 'effect/Function';
import { ApiClient } from '@/api/client';
import {
  GroupsResponseSchema,
  UsersInGroupResponseSchema,
  GroupResponseSchema,
  InvitationResponseSchema,
  type Group,
  type GroupWithMembers,
  type Invitation,
} from '@/api/schemas/group.schema';
import { type User } from '@/api/schemas/user.schema';

export interface CreateGroupParams {
  name: string;
}

export interface UpdateGroupParams {
  groupId: string;
  name: string;
}

export interface SetPinnedCategoriesParams {
  groupId: string;
  categoryIds: string[];
}

export interface CategoryBalance {
  categoryId: string;
  categoryName: string;
  categoryEmoji: string;
  balances: Array<{
    userId: string;
    userName: string;
    amount: number;
  }>;
}

export interface CategoryBalancesResponse {
  categories: CategoryBalance[];
}

export class GroupService extends Context.Tag('GroupService')<
  GroupService,
  {
    readonly create: (params: CreateGroupParams) => Effect.Effect<Group, any>;
    readonly update: (params: UpdateGroupParams) => Effect.Effect<Group, any>;
    readonly fetchUserGroups: () => Effect.Effect<GroupWithMembers[], any>;
    readonly fetchUsersInGroup: (groupId: string) => Effect.Effect<User[], any>;
    readonly setPinnedCategories: (
      params: SetPinnedCategoriesParams
    ) => Effect.Effect<Group, any>;
    readonly fetchCategoryBalances: (
      groupId: string
    ) => Effect.Effect<CategoryBalancesResponse, any>;
    readonly createInvitation: (groupId: string) => Effect.Effect<Invitation, any>;
    readonly addUserToGroup: (
      userId: string,
      groupId: string
    ) => Effect.Effect<void, any>;
  }
>() {}

export const GroupServiceLive = Layer.effect(
  GroupService,
  Effect.gen(function* (_) {
    const api = yield* _(ApiClient);

    return {
      create: (params) =>
        pipe(
          api.post('/groups/create', { name: params.name }, GroupResponseSchema),
          Effect.map((response) => response.group),
          Effect.tap(() => Effect.log(`Group created: ${params.name}`))
        ),

      update: (params) =>
        pipe(
          api.post(
            `/groups/${params.groupId}/update`,
            { name: params.name },
            GroupResponseSchema
          ),
          Effect.map((response) => response.group),
          Effect.tap(() => Effect.log(`Group ${params.groupId} updated: ${params.name}`))
        ),

      fetchUserGroups: () =>
        pipe(
          api.get('/users/me/groups', GroupsResponseSchema),
          Effect.map((response) => response.groups),
          Effect.tap((groups) =>
            Effect.log(`Fetched ${groups.length} groups for current user`)
          )
        ),

      fetchUsersInGroup: (groupId) =>
        pipe(
          api.get(`/groups/${groupId}/users`, UsersInGroupResponseSchema),
          Effect.map((response) => response.users),
          Effect.tap((users) =>
            Effect.log(`Fetched ${users.length} users in group ${groupId}`)
          )
        ),

      setPinnedCategories: (params) => {
        const request = {
          categories: params.categoryIds,
        };

        return pipe(
          api.post(
            `/groups/${params.groupId}/setPinnedCategories`,
            request,
            GroupResponseSchema
          ),
          Effect.map((response) => response.group),
          Effect.tap(() =>
            Effect.log(
              `Updated pinned categories for group ${params.groupId}: ${params.categoryIds.length} categories`
            )
          )
        );
      },

      fetchCategoryBalances: (groupId) => {
        console.log('[GroupService] fetchCategoryBalances called with:', groupId);
        return pipe(
          api.get(`/groups/${groupId}/category-balances`),
          Effect.map((response: any) => response as CategoryBalancesResponse),
          Effect.tap((response) =>
            Effect.log(
              `Fetched category balances for group ${groupId}: ${response.categories.length} categories`
            )
          )
        );
      },

      createInvitation: (groupId) =>
        pipe(
          api.post(`/groups/${groupId}/invite`, {}, InvitationResponseSchema),
          Effect.map((response) => response.invitation),
          Effect.tap(() => Effect.log(`Created invitation for group ${groupId}`))
        ),

      addUserToGroup: (userId, groupId) =>
        pipe(
          api.post(`/users/${userId}/groups/${groupId}`, {}),
          Effect.tap(() =>
            Effect.log(`Added user ${userId} to group ${groupId}`)
          )
        ),
    };
  })
);